import { inject, Injectable, Injector } from '@angular/core';
import { WorkflowConfigService } from '../workflow-config/workflow-config.service';
import { firstValueFrom } from 'rxjs';
import {
  ActiveConfig,
  ContainerType,
  formatNameAsUrl,
  PageNavigationMap,
  PageNavigationMetaData,
  SupportedDevice,
  WorkflowConfig,
  WorkflowPageConfig,
  WorkflowZoneDevicesConfig,
} from '@finxone-platform/shared/sys-config-types';
import { isEqual } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class PageNavigationMapService {
  private readonly injector = inject(Injector);

  public createPageNavigationMap(workflowConfig: WorkflowConfig): WorkflowConfig {
    const roles = workflowConfig.permissionSections?.roles;
    if (!roles) return workflowConfig;
    const pageMap: PageNavigationMap = {};
    roles?.forEach((role) => {
      Object.entries(role.zones).forEach(([zoneKey, zone]) => {
        if (zone === null) return;

        const config = zone.canvases.subscription?.['default'];
        if (!config) return;

        const workflowZoneDevicesConfig = config as WorkflowZoneDevicesConfig;
        this.processDevicePages(pageMap, workflowZoneDevicesConfig, zoneKey, role.slug);
      });
    });
    workflowConfig.pageNavigationMap = pageMap;
    return workflowConfig;
  }

  private processDevicePages(
    pageMap: PageNavigationMap,
    workflowZoneDevicesConfig: WorkflowZoneDevicesConfig,
    zoneKey: string,
    roleSlug: string,
  ): void {
    Object.keys(workflowZoneDevicesConfig).forEach((deviceKey) => {
      const key = deviceKey as keyof WorkflowZoneDevicesConfig;
      const pages = workflowZoneDevicesConfig[key]?.pages;
      pages?.forEach((page) => {
        if (!page.pageUniqueId) {
          return;
        }
        if (!pageMap[key]) {
          pageMap[key] = {};
        }
        const deviceMap = pageMap[key];
        // Assuming pageUniqueId is unique across all pages, if not then we need to come up with a unique key for each page, for now using pageUniqueId as key to store page metadata in map.
        const pageMetaData: PageNavigationMetaData = {
          pageId: page.pageUniqueId,
          _id: page.pageUniqueId,
          pageUrl: page.pageConfiguration?.url || page.pageConfiguration?.fullUrls?.[0] || '',
          name: page.name || '',
          title: page.title || '',
          containerType: page?.containerType || ContainerType.PAGE,
          zone: zoneKey,
          persona: formatNameAsUrl(roleSlug),
          device: key,
        };
        if (!deviceMap[page.pageUniqueId] || !isEqual(deviceMap[page.pageUniqueId], pageMetaData)) {
          deviceMap[page.pageUniqueId] = pageMetaData;
        }
      });
    });
  }

  public async removePageDataFromNavigationMap(pageId: string, device: SupportedDevice) {
    const workflowConfigService = this.injector.get(WorkflowConfigService);
    const workflowConfig = await firstValueFrom(workflowConfigService.getConfig());
    const pageNavigationMap = workflowConfig?.pageNavigationMap;
    if (!pageNavigationMap) return;
    if (!pageNavigationMap?.[device]?.[pageId]) return;
    delete pageNavigationMap[device][pageId];
    this.updatePageNavigationMapInWorkflowConfig(pageNavigationMap);
  }

  public async updatePageDataInNavigationMap(
    pageId: string,
    device: SupportedDevice,
    pageMetaData: PageNavigationMetaData,
  ) {
    const workflowConfigService = this.injector.get(WorkflowConfigService);
    const workflowConfig = await firstValueFrom(workflowConfigService.getConfig());
    let pageNavigationMap = workflowConfig?.pageNavigationMap;
    if (!pageNavigationMap) return;
    const page = pageNavigationMap[device][pageId];
    if (page) {
      pageNavigationMap[device][pageId] = pageMetaData;
    } else {
      pageNavigationMap = {
        ...pageNavigationMap,
        [device]: {
          ...pageNavigationMap[device],
          [pageId]: pageMetaData,
        },
      };
    }
    this.updatePageNavigationMapInWorkflowConfig(pageNavigationMap);
  }

  private updatePageNavigationMapInWorkflowConfig(pageNavigationMap: PageNavigationMap) {
    const workflowConfigService = this.injector.get(WorkflowConfigService);
    workflowConfigService.updateConfig((config) => {
      config.pageNavigationMap = pageNavigationMap;
      return config;
    });
  }

  public createPageNavigationMetadataForPage(
    pageConfig: WorkflowPageConfig,
    activeConfig: ActiveConfig,
  ): PageNavigationMetaData {
    const navigationMetadata: PageNavigationMetaData = {
      pageId: pageConfig.pageUniqueId as string,
      _id: pageConfig.pageUniqueId as string,
      pageUrl: pageConfig.pageConfiguration.url,
      name: pageConfig.name,
      title: pageConfig.title,
      zone: activeConfig.activeZone,
      device: activeConfig.activeDevice,
      persona: formatNameAsUrl(activeConfig.activeRole),
      containerType: pageConfig.containerType,
    };
    return navigationMetadata;
  }

  public async handlePageJsonUpdateAndUpdateMap(
    oldPageConfig: WorkflowPageConfig,
    updatedPageConfig: WorkflowPageConfig,
    activeConfig: ActiveConfig,
  ) {
    if (oldPageConfig.pageUniqueId !== updatedPageConfig.pageUniqueId) {
      await this.removePageDataFromNavigationMap(
        oldPageConfig.pageUniqueId as string,
        activeConfig.activeDevice,
      );
    }
    const newPageMetaData = this.createPageNavigationMetadataForPage(updatedPageConfig, activeConfig);
    await this.updatePageDataInNavigationMap(
      updatedPageConfig.pageUniqueId as string,
      activeConfig.activeDevice,
      newPageMetaData,
    );
  }
}
